import styled from 'styled-components';
import { Dispatch, SetStateAction } from 'react';

import { Title } from '@components/atoms';
import { Colors } from '@shared/interfaces/themes';

type ButtonProps<T> = {
  id?: T;
  height: string;
  width: string;
  bgColor: keyof Colors;
  gradient?: boolean;
  color: keyof Colors;
  title: string;
  shadow?: string;
  justifyContent?: 'center' | 'flex-start' | 'flex-end';
  padding?: string;
  margin?: string;
  disabled?: boolean;
  fontSize?: string;
  Icon?: SVGElement | string;
  onClick?: () => void;
  setStateAction?: Dispatch<SetStateAction<T>>;
  iconHeight?: number;
  iconWidth?: number;
};

function Button<T>({
  height,
  width,
  bgColor,
  gradient,
  setStateAction,
  onClick,
  id,
  color,
  disabled = false,
  title,
  shadow,
  justifyContent = 'center',
  padding,
  margin,
  fontSize,
  Icon,
  iconWidth,
  iconHeight,
}: ButtonProps<T>) {
  const onButtonClick = () => {
    if (disabled) return;

    if (setStateAction) setStateAction(id!);
    if (onClick) onClick();
  };

  return (
    <StyledButton
      $height={height}
      $width={width}
      $bgColor={bgColor}
      $color={color}
      $shadow={shadow}
      $gradient={gradient}
      $margin={margin}
      $disabled={disabled}
      onClick={onButtonClick}
    >
      <Title
        title={title}
        padding={padding}
        fontSize={fontSize}
        justifyContent={justifyContent}
        Icon={Icon}
        iconWidth={iconWidth}
        iconHeight={iconHeight}
      />
    </StyledButton>
  );
}

const StyledButton = styled.button<{
  $height: string;
  $width: string;
  $shadow?: string;
  $bgColor: keyof Colors;
  $gradient?: boolean;
  $disabled?: boolean;
  $color: keyof Colors;
  $margin?: string;
}>`
  border-radius: 25px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  border: 0;

  transition: background-color 0.5s ease;
  box-shadow: ${({ $shadow }) => $shadow};
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  color: ${({ theme, $color }) => theme.colors[$color]};

  mask-image: ${({ $gradient }) =>
    $gradient &&
    'linear-gradient(to bottom, #000000ff, #000000ff, #000000CC)'}; //TODO ????

  margin: ${({ $margin }) => $margin};
  background-color: ${({ theme, $bgColor }) => theme.colors[$bgColor]};

  &:hover {
    mask-image: ${({ $gradient }) =>
      $gradient && 'linear-gradient(to top, #000000ff, #000000ff)'};
  }
`;

export default Button;
